import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  CalculatorOutlined,
  EditOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { Card } from "reactstrap";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import ProjectScopeSelect from "../shared/ProjectScopeSelect";
import "./execution.css";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import {
  ExecutionBoqLine,
  ExecutionNormCell,
  ExecutionNormSaveMode,
  ExecutionNormVersion,
  ExecutionSlotDef,
  ExecutionSlotKey,
  executionNormApis,
} from "../../../api/ActivityExecution";
import {
  DEFAULT_SLOT_LABELS,
  EXECUTION_SLOT_KEYS,
  boqNoOf,
} from "./executionNorm";
import ExecutionNormsUploadModal from "./ExecutionNormsUploadModal";
import RecalculateNormsModal from "./RecalculateNormsModal";
import { useWbsRecordName } from "../shared/useWbsCodes";

// Execution Norms — the activity x BOQ line x driver slot matrix.
//
// A norm is "how much of this BOQ line one unit of advance in this slot
// produces": B1.1 Dewatering = 327.2590 KWh per metre of Class-III advance.
//
// The single most important UI rule here: a BLANK cell means "this BOQ line does
// not apply to that class" and a 0 means "configured as zero". They are
// different statements and the arithmetic treats them differently — so a blank
// renders as a muted dash and is never coerced to 0.
//
// The matrix is EFFECTIVE-DATED, so this screen never shows "the norms" — it
// shows the norms as of a version. Saving therefore asks a question the user is
// the only one who can answer:
//
//   Correct this version  — a typo. The fix reaches days already captured
//                           under it, which is what you want when the matrix
//                           was simply keyed wrong.
//   New version from ...  — the norm genuinely changed. Days already captured
//                           keep the figures they were derived with.
//
// Guessing either way silently moves — or silently fails to move — numbers that
// have been reported, so there is no default that is safe to hide.

type NormDraft = Record<number, Partial<Record<ExecutionSlotKey, string>>>;

const ExecutionNormsPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  // This lives under Norms (org level), so a project is picked here rather than
  // inherited from project chrome. A :projectId in the URL — how the Daily
  // Execution screen deep-links in — wins and preselects the scope.
  const routeProjectId = Number(params.projectId) || undefined;
  const [projectId, setProjectId] = useState<number | undefined>(routeProjectId);
  const [phaseId, setPhaseId] = useState<number | undefined>();
  const initialActivityId = (location.state as any)?.activityId;

  const [activities, setActivities] = useState<any[]>([]);
  const [activityId, setActivityId] = useState<number | undefined>(initialActivityId);
  const [slots, setSlots] = useState<ExecutionSlotDef[]>([]);
  const [tasks, setTasks] = useState<ExecutionBoqLine[]>([]);
  const [draft, setDraft] = useState<NormDraft>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [slotModalOpen, setSlotModalOpen] = useState(false);
  const [slotDraft, setSlotDraft] = useState<ExecutionSlotDef[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // --- versioning ---
  const [versions, setVersions] = useState<ExecutionNormVersion[]>([]);
  // Which version the matrix below IS. undefined = the newest, which is what
  // the screen opens on.
  const [viewVersionId, setViewVersionId] = useState<number | undefined>();
  const [shownVersion, setShownVersion] = useState<{
    versionId: number;
    effectiveFrom: string;
    remarks: string;
    isEditable: boolean;
  } | null>(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveMode, setSaveMode] = useState<ExecutionNormSaveMode>("newVersion");
  const [saveEffectiveFrom, setSaveEffectiveFrom] = useState<Dayjs | null>(null);
  const [saveRemarks, setSaveRemarks] = useState("");
  const [recalcOpen, setRecalcOpen] = useState(false);

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({ ...item, linkState: location.state }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    executionNormApis
      .listActivities(projectId)
      .then((res) => {
        if (cancelled || res?.status !== true) return;
        const list = res.data?.activities || [];
        setActivities(list);
        setActivityId((current) => current ?? list[0]?.activityId);
      })
      .catch(() => !cancelled && setError("Could not load activities"));
    return () => {
      cancelled = true;
    };
  }, [projectId, refreshTick]);

  // A version id belongs to ONE activity's chain, so it must not survive a
  // change of activity — carrying it over would 404, or worse, silently show a
  // neighbouring face's matrix.
  useEffect(() => {
    setViewVersionId(undefined);
  }, [activityId]);

  const load = useCallback(() => {
    if (!activityId) return;
    let cancelled = false;
    setLoading(true);
    executionNormApis
      .getByActivity(activityId, viewVersionId)
      .then((res) => {
        if (cancelled) return;
        if (res?.status !== true) {
          setError(res?.message || "Could not load execution norms");
          return;
        }
        setError("");
        const data = res.data || {};
        setSlots(data.slots || []);
        setTasks(data.tasks || []);
        setVersions(data.versions || []);
        setShownVersion(
          data.versionId
            ? {
                versionId: Number(data.versionId),
                effectiveFrom: data.effectiveFrom || "",
                remarks: data.versionRemarks || "",
                isEditable: data.isEditable !== false,
              }
            : null
        );
        // Sparse by design: only cells the server sent become entries. An
        // absent cell stays absent, which is how "not applicable" survives a
        // load-edit-save round trip.
        const next: NormDraft = {};
        (data.norms || []).forEach((cell: ExecutionNormCell) => {
          next[cell.taskId] = { ...(next[cell.taskId] || {}), [cell.slotKey]: cell.normValue };
        });
        setDraft(next);
      })
      .catch(() => !cancelled && setError("Could not load execution norms"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activityId, viewVersionId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load, refreshTick]);

  const activityLabel = useWbsRecordName(activities, "activityId", "activityName");

  // 50 activities is too many to scan, so a phase narrows the list first. Both
  // come from the one activities call — no extra request.
  const phaseOptions = useMemo(() => {
    const seen = new Map<number, string>();
    activities.forEach((a: any) => {
      if (a.phaseId && !seen.has(a.phaseId)) seen.set(a.phaseId, a.phaseName || `Phase ${a.phaseId}`);
    });
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
  }, [activities]);

  const activityOptions = useMemo(
    () =>
      activities
        .filter((a: any) => !phaseId || a.phaseId === phaseId)
        .map((a: any) => ({ value: a.activityId, label: activityLabel(a) })),
    [activities, phaseId, activityLabel]
  );
  const activeSlots = useMemo(() => slots.filter((slot) => slot.isActive !== false), [slots]);

  const cellCount = useMemo(
    () =>
      Object.values(draft).reduce(
        (acc, row) => acc + Object.values(row || {}).filter((value) => value !== undefined).length,
        0
      ),
    [draft]
  );

  const setCell = (taskId: number, slotKey: ExecutionSlotKey, value: string | null) => {
    setDraft((current) => {
      const row = { ...(current[taskId] || {}) };
      if (value === null || value === undefined || value === "") {
        // Clearing a cell must DELETE it, not write '0' — blank is "not
        // applicable", zero is a configured value.
        delete row[slotKey];
      } else {
        row[slotKey] = value;
      }
      return { ...current, [taskId]: row };
    });
  };

  // Older versions are history, and history that can be rewritten is not
  // history. Correcting one means dating a NEW version over it.
  const readOnly = !!shownVersion && shownVersion.isEditable === false;

  const openSaveModal = () => {
    if (!activityId) return;
    // Default to a new version: on an activity that already has one, an edit is
    // far more often a real revision than a typo, and the safer default is the
    // one that cannot retroactively change a reported month.
    setSaveMode(shownVersion ? "newVersion" : "correct");
    setSaveEffectiveFrom(dayjs());
    setSaveRemarks("");
    setSaveModalOpen(true);
  };

  const handleSave = async () => {
    if (!activityId) return;
    if (saveMode === "newVersion" && !saveEffectiveFrom) {
      message.error("Pick the date the new version takes effect");
      return;
    }
    setSaving(true);
    try {
      const norms: ExecutionNormCell[] = [];
      Object.entries(draft).forEach(([taskId, row]) => {
        Object.entries(row || {}).forEach(([slotKey, normValue]) => {
          if (normValue === undefined || normValue === "") return;
          norms.push({
            taskId: Number(taskId),
            slotKey: slotKey as ExecutionSlotKey,
            normValue: String(normValue),
          });
        });
      });
      const res = await executionNormApis.save(activityId, slots, norms, {
        mode: saveMode,
        ...(saveMode === "newVersion" && saveEffectiveFrom
          ? { effectiveFrom: saveEffectiveFrom.format("YYYY-MM-DD") }
          : {}),
        remarks: saveRemarks.trim(),
      });
      if (res?.status !== true) {
        message.error(res?.message || "Could not save the execution norms");
        return;
      }
      message.success(res.data?.message || "Execution norms saved");
      // A back-dated version leaves already-captured days holding their old
      // figures. Say so — and keep saying so until someone restates them.
      if (res.data?.warning) {
        message.warning({ content: res.data.warning, duration: 8 });
      }
      setSaveModalOpen(false);
      // Snap back to the newest version, which is the one just written.
      setViewVersionId(undefined);
      setRefreshTick((tick) => tick + 1);
    } catch (err: any) {
      message.error(err?.message || "Could not save the execution norms");
    } finally {
      setSaving(false);
    }
  };

  const openSlotModal = () => {
    setSlotDraft(
      EXECUTION_SLOT_KEYS.map((slotKey, index) => {
        const existing = slots.find((slot) => slot.slotKey === slotKey);
        return {
          slotKey,
          slotLabel: existing?.slotLabel || DEFAULT_SLOT_LABELS[slotKey],
          uomId: existing?.uomId,
          isActive: existing?.isActive ?? false,
          displayOrder: existing?.displayOrder ?? index,
        };
      })
    );
    setSlotModalOpen(true);
  };

  const applySlotDraft = () => {
    setSlots(slotDraft);
    // Deactivating a slot orphans its norms — drop them now so the saved matrix
    // matches what the screen shows.
    const disabled = new Set(
      slotDraft.filter((slot) => slot.isActive === false).map((slot) => slot.slotKey)
    );
    if (disabled.size) {
      setDraft((current) => {
        const next: NormDraft = {};
        Object.entries(current).forEach(([taskId, row]) => {
          const kept: any = {};
          Object.entries(row || {}).forEach(([slotKey, value]) => {
            if (!disabled.has(slotKey as ExecutionSlotKey)) kept[slotKey] = value;
          });
          next[Number(taskId)] = kept;
        });
        return next;
      });
    }
    setSlotModalOpen(false);
  };

  const columns = useMemo(
    () => [
      {
        // Read taskSequenceId, NOT internalTaskNumber: the latter is the bare
        // 'B3.1.1' and repeats for a BOQ line split across two tasks, which
        // would render two identical-looking rows. The sequence id carries the
        // disambiguating '(1)'.
        title: "Task #",
        dataIndex: "taskSequenceId",
        width: 140,
        fixed: "left" as const,
        render: (value: string, row: ExecutionBoqLine) => (
          <span style={{ fontWeight: 600, color: "#022c57" }}>
            {boqNoOf(value) || row.internalTaskNumber || "—"}
          </span>
        ),
      },
      {
        title: "Description",
        dataIndex: "taskName",
        width: 280,
        ellipsis: true,
      },
      {
        title: "UOM",
        dataIndex: "workUomName",
        width: 80,
        render: (value: string) => value || "—",
      },
      ...activeSlots.map((slot) => ({
        title: slot.slotLabel,
        key: slot.slotKey,
        width: 150,
        align: "right" as const,
        render: (_: any, row: ExecutionBoqLine) => (
          <InputNumber
            value={
              draft[row.taskId]?.[slot.slotKey] !== undefined
                ? Number(draft[row.taskId]?.[slot.slotKey])
                : null
            }
            onChange={(value) =>
              setCell(row.taskId, slot.slotKey, value === null ? null : String(value))
            }
            // Superseded versions render read-only rather than hidden: the point
            // of history is being able to read it.
            disabled={readOnly}
            // Blank is meaningful — it is how "not applicable to this class" is
            // expressed. Do not show a 0 placeholder here.
            placeholder="—"
            controls={false}
            style={{ width: "100%" }}
          />
        ),
      })),
    ],
    [activeSlots, draft, readOnly]
  );

  return (
    <>
      <Head title="Execution Norms" />
      <Content>
        <Card className="card-bordered">
          <div className="card-inner card-inner-lg">
            <div className="pms-exec-header">
              <h5 className="pms-exec-title">Execution Norms</h5>
              <div className="pms-exec-bar">
                <ProjectScopeSelect
                  value={projectId}
                  onChange={(next) => {
                    setProjectId(next);
                    setPhaseId(undefined);
                    setActivityId(undefined);
                  }}
                  hideOrgMaster
                  placeholder="Select a project"
                  style={{ minWidth: 240 }}
                />
                <Button
                  icon={<UploadOutlined />}
                  disabled={!projectId}
                  onClick={() => setUploadOpen(true)}
                >
                  Upload Excel
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshTick((t) => t + 1)}
                />
              </div>
            </div>
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

            {!projectId && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Pick a project to configure its execution norms"
              />
            )}

        {!!projectId && (
          <>
            {/* One card only — this content already sits inside the page's
                card-inner, so a SectionCard here nested a box in a box. */}
            <div className="pms-exec-toolbar">
              <div className="pms-exec-bar">
                <Select
                  value={phaseId}
                  onChange={(v) => {
                    setPhaseId(v);
                    setActivityId(undefined);
                  }}
                  style={{ width: 230 }}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="All phases"
                  options={phaseOptions}
                />
                <Select
                  value={activityId}
                  onChange={setActivityId}
                  style={{ width: 340 }}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select an activity"
                  options={activityOptions}
                />
                {cellCount > 0 && <Tag color="processing">{cellCount} cells</Tag>}
              </div>
              <div className="pms-exec-bar">
                <Button
                  icon={<CalculatorOutlined />}
                  disabled={!activityId}
                  onClick={() => setRecalcOpen(true)}
                >
                  Restate days
                </Button>
                <Button
                  icon={<EditOutlined />}
                  disabled={!activityId || readOnly}
                  onClick={openSlotModal}
                >
                  Edit slots
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={saving}
                  disabled={!activityId || readOnly}
                  onClick={openSaveModal}
                >
                  Save
                </Button>
              </div>
            </div>

            {/* WHICH matrix is on screen. Never implicit — every number below is
                only true as of a date. */}
            {!!activityId && !!shownVersion && (
              <div className="pms-exec-version-bar">
                <span className="pms-exec-version-label">
                  <HistoryOutlined /> Version in view
                </span>
                <Select
                  value={shownVersion.versionId}
                  onChange={(value) =>
                    setViewVersionId(
                      versions[0] && value === versions[0].versionId ? undefined : value
                    )
                  }
                  style={{ width: 320 }}
                  options={versions.map((version) => ({
                    value: version.versionId,
                    label: `${dayjs(version.effectiveFrom).format("DD-MMM-YYYY")} → ${
                      version.effectiveTo
                        ? dayjs(version.effectiveTo).format("DD-MMM-YYYY")
                        : "current"
                    }`,
                  }))}
                />
                {shownVersion.isEditable ? (
                  <Tag color="green">In force</Tag>
                ) : (
                  <Tag color="default">Superseded — read only</Tag>
                )}
                {!!shownVersion.remarks && (
                  <Tooltip title={shownVersion.remarks}>
                    <span className="pms-exec-version-remarks">
                      {shownVersion.remarks}
                    </span>
                  </Tooltip>
                )}
              </div>
            )}

            {/* Reading history is fine; editing it is not. Say which, and say
                what to do instead. */}
            {readOnly && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="You are looking at a superseded version"
                description="Older versions are read-only. To change what applies from a date, switch to the current version and save a new one effective from that date."
                action={
                  <Button size="small" onClick={() => setViewVersionId(undefined)}>
                    Go to current
                  </Button>
                }
              />
            )}

            {!activityId ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Pick an activity to configure its norm matrix"
              />
            ) : (
              <>
          <div style={{ color: "#8094ae", fontSize: 12, marginBottom: 12 }}>
            A norm is the BOQ quantity produced per <strong>one unit</strong> of advance in that
            slot{activeSlots[0]?.uomName ? ` (${activeSlots[0].uomName})` : ""}. Leave a cell blank
            when the BOQ line does not apply to that class — blank and 0 are different, and only 0
            counts as a configured value.
          </div>

          {activeSlots.length === 0 ? (
            <Empty description="No driver slots are active for this activity">
              <Button type="primary" onClick={openSlotModal}>
                Configure slots
              </Button>
            </Empty>
          ) : (
            <Table
              size="small"
              rowKey="taskId"
              loading={loading}
              dataSource={tasks}
              columns={columns as any}
              scroll={{ x: 900 + activeSlots.length * 150 }}
              pagination={{ pageSize: 25, showSizeChanger: false }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No BOQ lines under this activity"
                  />
                ),
              }}
            />
          )}
              </>
            )}
          </>
        )}
          </div>
        </Card>
      </Content>

      {/* Modals live as SIBLINGS of the shell so a loading refresh can never
          unmount them mid-flow. */}
      <Modal
        open={slotModalOpen}
        title="Driver slots"
        onCancel={() => setSlotModalOpen(false)}
        onOk={applySlotDraft}
        okText="Apply"
        width={640}
      >
        <div style={{ color: "#8094ae", fontSize: 12, marginBottom: 12 }}>
          Most activities drive off rock classes measured in metres of advance. Caverns and shafts
          excavated by method or zone (Power House, Transformer Cavern, Surge Shaft, Pressure Shaft,
          GOC/DT Shaft) relabel these slots to their own drivers — relabelling here does not move any
          production already captured, because slots are identified internally, not by name.
        </div>
        <Table
          size="small"
          rowKey="slotKey"
          pagination={false}
          dataSource={slotDraft}
          columns={[
            {
              title: "Use",
              width: 60,
              render: (_: any, slot: ExecutionSlotDef) => (
                <Checkbox
                  checked={slot.isActive !== false}
                  onChange={(event) =>
                    setSlotDraft((current) =>
                      current.map((item) =>
                        item.slotKey === slot.slotKey
                          ? { ...item, isActive: event.target.checked }
                          : item
                      )
                    )
                  }
                />
              ),
            },
            { title: "Slot", dataIndex: "slotKey", width: 90 },
            {
              title: "Label",
              render: (_: any, slot: ExecutionSlotDef) => (
                <Input
                  value={slot.slotLabel}
                  maxLength={150}
                  onChange={(event) =>
                    setSlotDraft((current) =>
                      current.map((item) =>
                        item.slotKey === slot.slotKey
                          ? { ...item, slotLabel: event.target.value }
                          : item
                      )
                    )
                  }
                />
              ),
            },
          ]}
        />
      </Modal>

      {/* The save question. Deliberately a stop rather than a silent default:
          only the user knows whether this edit is a typo or a revision, and the
          two have opposite effects on months already reported. */}
      <Modal
        open={saveModalOpen}
        title="Save execution norms"
        onCancel={() => setSaveModalOpen(false)}
        onOk={handleSave}
        okText="Save"
        confirmLoading={saving}
        width={620}
      >
        <Radio.Group
          value={saveMode}
          onChange={(event) => setSaveMode(event.target.value)}
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          <Radio value="newVersion">
            <strong>Start a new version</strong>
            <div style={{ color: "#8094ae", fontSize: 12 }}>
              The norm genuinely changed. Days captured before the date below keep
              the figures they were derived with.
            </div>
          </Radio>
          {saveMode === "newVersion" && (
            <div style={{ paddingLeft: 24 }}>
              <DatePicker
                value={saveEffectiveFrom}
                onChange={setSaveEffectiveFrom}
                format="DD-MMM-YYYY"
                allowClear={false}
                style={{ width: 220 }}
              />
              {!!saveEffectiveFrom && saveEffectiveFrom.isBefore(dayjs(), "day") && (
                <div style={{ color: "#b76e00", fontSize: 12, marginTop: 6 }}>
                  This is a back-dated version. Days already captured on or after
                  it will keep their existing figures until you restate them.
                </div>
              )}
            </div>
          )}
          <Radio value="correct" disabled={!shownVersion}>
            <strong>
              Correct the current version
              {shownVersion
                ? ` (effective ${dayjs(shownVersion.effectiveFrom).format("DD-MMM-YYYY")})`
                : ""}
            </strong>
            <div style={{ color: "#8094ae", fontSize: 12 }}>
              The matrix was keyed wrong. The fix reaches every day already
              captured under this version.
            </div>
          </Radio>
        </Radio.Group>

        <div style={{ marginTop: 18 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Why it changed
          </div>
          <Input.TextArea
            value={saveRemarks}
            onChange={(event) => setSaveRemarks(event.target.value)}
            maxLength={500}
            rows={2}
            placeholder="e.g. revised Class-III productivity after the Aug face review"
          />
        </div>
      </Modal>

      <RecalculateNormsModal
        open={recalcOpen}
        activityId={activityId || 0}
        activityLabel={
          activityOptions.find((option) => option.value === activityId)?.label || ""
        }
        onClose={() => setRecalcOpen(false)}
        onApplied={() => setRefreshTick((tick) => tick + 1)}
      />

      <ExecutionNormsUploadModal
        open={uploadOpen}
        projectId={projectId || 0}
        onClose={() => setUploadOpen(false)}
        onSuccess={() => setRefreshTick((tick) => tick + 1)}
      />
    </>
  );
};

export default ExecutionNormsPage;
